/**
 * AlgorandMonitor Page
 *
 * Monitor Algorand accounts, participation status, assets, and transactions.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useAlgorandWallets } from '../hooks/useAlgorandWallet';
import { useAlgorandMonitorWallets } from '../hooks/useMonitorWallets';
import { isValidAlgorandAddress, algorandAPI } from '../services/algorandApi';
import { useToast } from '../components/Toast';
import { AlgorandHealthMetrics } from '../components/algorand/AlgorandHealthMetrics';
import { AlgoSupplyBreakdown } from '../components/algorand/AlgoSupplyBreakdown';
import {
  NetworkMonitorPageHeader,
  NetworkAddressInputForm,
  NetworkMonitorEmptyState,
  MonitorTabs,
} from '../components/monitor';
import { ResponsiveTable } from '../components/ui/ResponsiveTable';
import type { AlgorandWalletData } from '../types/algorand.types';

const ALGO_COLORS = {
  primary: '#000000',
  secondary: '#6366f1',
  accent: '#8b5cf6',
  gradient: 'linear-gradient(135deg, #000000, #333333)',
  lightGradient: 'linear-gradient(135deg, rgba(0, 0, 0, 0.15), rgba(51, 51, 51, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
};

type TabType = 'overview' | 'transactions' | 'assets';

const s = {
  page: { padding: '0', maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '20px', flexWrap: 'wrap' as const },
  title: { display: 'flex', alignItems: 'center', gap: '12px' },
  titleText: { fontSize: '24px', fontWeight: 700, margin: 0 },
  subtitle: { fontSize: '13px', color: 'var(--muted)', marginTop: '4px' },
  card: { background: 'rgba(0, 0, 0, 0.25)', backdropFilter: 'blur(20px)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '20px', marginBottom: '20px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: (color: string) => ({ background: `${color}10`, border: `1px solid ${color}30`, borderRadius: '12px', padding: '16px', textAlign: 'center' as const }),
  statValue: { fontSize: '22px', fontWeight: 700 },
  statLabel: { fontSize: '11px', color: 'var(--muted)', marginTop: '4px', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  input: { width: '100%', padding: '12px 14px', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', color: 'var(--text)', fontSize: '13px', fontFamily: 'monospace', outline: 'none' },
  button: { padding: '12px 20px', background: ALGO_COLORS.gradient, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' },
  buttonSecondary: { padding: '12px 20px', background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  tabContainer: { display: 'flex', gap: '4px', padding: '4px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '14px', width: 'fit-content', marginBottom: '24px' },
  tab: (isActive: boolean) => ({ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s ease', background: isActive ? ALGO_COLORS.gradient : 'transparent', color: isActive ? 'white' : 'var(--muted)' }),
  emptyState: { textAlign: 'center' as const, padding: '60px 20px' },
};

function NetworkStatsBanner({ algoPrice, totalAccounts, isLoading }: { algoPrice: number; totalAccounts: number; isLoading: boolean }) {
  const { t } = useTranslation();
  return (
    <div style={{ ...s.card, background: ALGO_COLORS.lightGradient, border: '1px solid rgba(0, 0, 0, 0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>{t('algorand.network.title', 'Algorand Network')}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700 }}>${algoPrice.toFixed(4)}</span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>ALGO</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{totalAccounts}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('algorand.network.accounts', 'Accounts')}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: isLoading ? ALGO_COLORS.warning : ALGO_COLORS.success, boxShadow: `0 0 10px ${isLoading ? ALGO_COLORS.warning : ALGO_COLORS.success}`, margin: '0 auto 4px' }} />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{isLoading ? t('algorand.network.syncing', 'Syncing...') : t('algorand.network.connected', 'Connected')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsOverview({ totalBalance, totalBalanceUsd, totalPendingRewards, totalPendingRewardsUsd, totalRewards, totalRewardsUsd, totalAssets, participatingCount }: { totalBalance: number; totalBalanceUsd: number; totalPendingRewards: number; totalPendingRewardsUsd: number; totalRewards: number; totalRewardsUsd: number; totalAssets: number; participatingCount: number }) {
  const { t } = useTranslation();
  const stats = [
    { label: t('algorand.stats.balance', 'Total Balance'), value: `${totalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ALGO`, subvalue: `$${totalBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: ALGO_COLORS.primary, icon: <Icons.Wallet size={16} /> },
    { label: t('algorand.stats.pending', 'Pending Rewards'), value: `${totalPendingRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} ALGO`, subvalue: `$${totalPendingRewardsUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: ALGO_COLORS.warning, icon: <Icons.Clock size={16} /> },
    { label: t('algorand.stats.received', 'Total Rewards'), value: `${totalRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })} ALGO`, subvalue: `$${totalRewardsUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: ALGO_COLORS.success, icon: <Icons.TrendingUp size={16} /> },
    { label: t('algorand.stats.assets', 'Assets'), value: totalAssets.toString(), subvalue: `${participatingCount} participating`, color: ALGO_COLORS.accent, icon: <Icons.Coins size={16} /> },
  ];
  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{stat.subvalue}</div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

function AccountCard({ wallet, onRemove }: { wallet: AlgorandWalletData; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ ...s.card, background: 'rgba(0, 0, 0, 0.08)', border: '1px solid rgba(0, 0, 0, 0.2)', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: ALGO_COLORS.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icons.Algorand size={20} color="white" />
          </div>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, marginBottom: '2px' }}>{wallet.address.slice(0, 8)}...{wallet.address.slice(-6)}</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: wallet.isParticipating ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)', color: wallet.isParticipating ? ALGO_COLORS.success : '#ef4444', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
              {wallet.isParticipating ? 'Online' : 'Offline'}
            </div>
          </div>
        </div>
        <button onClick={onRemove} style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
          <Icons.Trash2 size={12} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('algorand.account.balance', 'Balance')}</div>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>{wallet.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ALGO</div>
          <div style={{ fontSize: '11px', color: ALGO_COLORS.success }}>${wallet.balanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('algorand.account.pending', 'Pending')}</div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: ALGO_COLORS.warning }}>{wallet.pendingRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} ALGO</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('algorand.account.assets', 'Assets')}</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{wallet.assets.length} ASAs</div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <NetworkMonitorEmptyState
      icon={<Icons.Algorand size={40} color="white" />}
      title={t('algorand.empty.title', 'Welcome to Algorand Monitor')}
      description={t(
        'algorand.empty.description',
        'Track your Algorand accounts, monitor participation status, view ASA holdings, and analyze transaction history.',
      )}
      colors={ALGO_COLORS}
    />
  );
}

export function AlgorandMonitor() {
  const { t } = useTranslation();
  const toast = useToast();
  const { wallets: accounts, setWallets: setAccounts } = useAlgorandMonitorWallets();
  const [newAccountInput, setNewAccountInput] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { wallets, isLoading, error, refetch, algoPrice, totalBalance, totalBalanceUsd, totalPendingRewards, totalPendingRewardsUsd, totalRewards, totalRewardsUsd, totalAssets, participatingCount } = useAlgorandWallets(accounts);

  const handleAddAccount = () => {
    const trimmedAddress = newAccountInput.trim();
    if (!trimmedAddress) { toast(t('algorand.toast.enterAddress', 'Please enter an address'), 'warning', 3000); return; }
    if (!isValidAlgorandAddress(trimmedAddress)) { toast(t('algorand.toast.invalidFormat', 'Invalid Algorand address format'), 'error', 4000); return; }
    if (accounts.includes(trimmedAddress)) { toast(t('algorand.toast.alreadyAdded', 'This address is already added'), 'warning', 3000); return; }
    setAccounts([...accounts, trimmedAddress]);
    setNewAccountInput('');
    setShowAddAccount(false);
    toast(t('algorand.toast.addressAdded', 'Address added successfully'), 'success', 3000);
  };

  const handleRemoveAccount = (address: string) => {
    if (confirm(t('algorand.confirm.removeAddress', `Remove address ${address.slice(0, 8)}...?`))) {
      setAccounts(accounts.filter(a => a !== address));
      toast(t('algorand.toast.addressRemoved', 'Address removed'), 'info', 3000);
    }
  };

  const isValidInput = newAccountInput.trim() && isValidAlgorandAddress(newAccountInput.trim());
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('algorand.tabs.overview', 'Overview'), icon: <Icons.BarChart size={16} /> },
    { id: 'transactions', label: t('algorand.tabs.transactions', 'Transactions'), icon: <Icons.ArrowRightLeft size={16} /> },
    { id: 'assets', label: t('algorand.tabs.assets', 'Assets'), icon: <Icons.Coins size={16} /> },
  ];

  const allTransactions = wallets.flatMap(w => w.recentTransactions.map(tx => ({ ...tx, walletAddress: w.address }))).sort((a, b) => b['round-time'] - a['round-time']);
  const assetMap = new Map<number, { assetId: number; name: string; unitName: string; balance: number; decimals: number }>();
  for (const wallet of wallets) {
    for (const asset of wallet.assets) {
      const existing = assetMap.get(asset.assetId);
      if (existing) { existing.balance += asset.balance; }
      else { assetMap.set(asset.assetId, { ...asset }); }
    }
  }
  const aggregatedAssets = Array.from(assetMap.values());

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={<Icons.Algorand size={36} />}
        title="Algorand Monitor"
        subtitle={t('algorand.subtitle', 'Monitor your Algorand accounts and ASA holdings')}
        primaryAction={
          showAddAccount
            ? {
                label: t('common.cancel', 'Cancel'),
                icon: <Icons.X size={16} />,
                onClick: () => setShowAddAccount(false),
                variant: 'secondary',
              }
            : {
                label: t('algorand.addAddress', 'Add Address'),
                icon: <Icons.Plus size={16} />,
                onClick: () => setShowAddAccount(true),
              }
        }
        refreshAction={
          accounts.length > 0
            ? {
                label: t('common.refresh', 'Refresh'),
                icon: <Icons.RefreshCw size={16} />,
                onClick: () => refetch(),
              }
            : undefined
        }
        colors={ALGO_COLORS}
      />

      {showAddAccount && (
        <NetworkAddressInputForm
          value={newAccountInput}
          onChange={setNewAccountInput}
          onSubmit={handleAddAccount}
          submitLabel={t('common.add', 'Add')}
          placeholder={t('algorand.placeholder', 'Enter Algorand address (58 characters)')}
          isValid={newAccountInput.trim() ? Boolean(isValidInput) : null}
          colors={ALGO_COLORS}
        />
      )}

      <NetworkStatsBanner algoPrice={algoPrice} totalAccounts={accounts.length} isLoading={isLoading} />

      {/* V2.K.5 — ALGO supply breakdown (distribution Foundation /
          online stake / free float / cap fixe 10 Md). Fed by
          CoinGecko, refreshes every 5 min. */}
      <AlgoSupplyBreakdown />

      {/* Blockchain Health Metrics */}
      <AlgorandHealthMetrics />

      {error && (
        <div style={{ ...s.card, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}><Icons.AlertTriangle size={20} /><span>{t('algorand.error', 'Error loading data')}: {(error as Error)?.message}</span></div>
        </div>
      )}

      {accounts.length > 0 ? (
        <>
          <StatsOverview totalBalance={totalBalance} totalBalanceUsd={totalBalanceUsd} totalPendingRewards={totalPendingRewards} totalPendingRewardsUsd={totalPendingRewardsUsd} totalRewards={totalRewards} totalRewardsUsd={totalRewardsUsd} totalAssets={totalAssets} participatingCount={participatingCount} />
          <MonitorTabs<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            colors={ALGO_COLORS}
            ariaLabel="Algorand monitor tabs"
          />

          {activeTab === 'overview' && (
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><Icons.Wallet size={18} />{t('algorand.accounts', 'Accounts')} ({wallets.length})</h2>
              {isLoading ? <div style={{ textAlign: 'center', padding: '40px' }}><Icons.Activity size={32} style={{ opacity: 0.3 }} /><p style={{ color: 'var(--muted)', marginTop: '12px' }}>{t('algorand.loading', 'Loading...')}</p></div> : wallets.map(wallet => <AccountCard key={wallet.address} wallet={wallet} onRemove={() => handleRemoveAccount(wallet.address)} />)}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>{t('algorand.transactions.title', 'Recent Transactions')}</h3>
              <ResponsiveTable
                data={allTransactions.slice(0, 50)}
                keyExtractor={(tx, idx) => `${tx.id}-${idx}`}
                emptyState={
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
                    {t('algorand.transactions.empty', 'No transactions found')}
                  </div>
                }
                columns={[
                  {
                    key: 'date',
                    header: 'Date',
                    isSubtitle: true,
                    render: (tx) => algorandAPI.formatTimestamp(tx['round-time']).toLocaleDateString(),
                  },
                  {
                    key: 'type',
                    header: 'Type',
                    isTitle: true,
                    render: (tx) => algorandAPI.getTransactionTypeName(tx['tx-type']),
                  },
                  {
                    key: 'amount',
                    header: 'Amount',
                    align: 'right',
                    render: (tx) => (
                      <span style={{ fontWeight: 600 }}>
                        {tx['payment-transaction'] ? `${(tx['payment-transaction'].amount / 1e6).toLocaleString()} ALGO` : '—'}
                      </span>
                    ),
                  },
                  {
                    key: 'fee',
                    header: 'Fee',
                    align: 'right',
                    render: (tx) => (
                      <span style={{ color: 'var(--muted)' }}>
                        {(tx.fee / 1e6).toFixed(6)} ALGO
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {activeTab === 'assets' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>{t('algorand.assets.title', 'ASA Holdings')} ({aggregatedAssets.length})</h3>
              {aggregatedAssets.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                  {aggregatedAssets.map(asset => (
                    <div key={asset.assetId} style={{ ...s.card, marginBottom: 0, background: 'rgba(139, 92, 246, 0.08)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: 600, color: ALGO_COLORS.accent }}>{asset.unitName || 'Unknown'}</div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{asset.name}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{asset.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                        </div>
                      </div>
                      <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--muted)', fontFamily: 'monospace' }}>ASA #{asset.assetId}</div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>{t('algorand.assets.empty', 'No assets found')}</div>}
            </div>
          )}
        </>
      ) : <EmptyState />}

      <div style={{ marginTop: '30px', padding: '16px 20px', background: 'rgba(0, 0, 0, 0.08)', borderRadius: '12px', border: '1px solid rgba(0, 0, 0, 0.15)', fontSize: '12px', color: 'var(--muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}><Icons.Info size={14} /><strong style={{ color: 'var(--text)' }}>{t('algorand.info.title', 'About Algorand Monitor')}</strong></div>
        <p style={{ lineHeight: 1.6 }}>{t('algorand.info.description', 'This page uses the Algonode public API to fetch account data. No wallet connection required.')}</p>
      </div>
    </div>
  );
}
